import { all, call, put, select, takeEvery } from "redux-saga/effects";

import {
    REACTION_CHANGE,
    ReactionChangeAction,
    reactionSave,
} from "@/mk2/containers/EmojiReaction/EmojiReaction.actions";
import { saveReaction as apiSaveReaction } from "@/mk2/containers/EmojiReaction/EmojiReaction.api";
import { EmojiLikeableEntity, likeKey } from "@/mk2/containers/Like/Like.schemas";
import {
    NormalizedResponse,
    XHRAction,
    XHRPostError,
    handleXHRPostErrorSaga,
} from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import { getEntitiesEntity } from "@/mk2/reducers";
import { checkIfUserIsLoggedIn } from "@/mk2/sagas/auth";
import { HistoryAction } from "@/mk2/services/browserHistory";

const logger = getLogger("EmojiReaction.sagas");

function computeOptimisticState(
    current: EmojiLikeableEntity,
    newEmojiType: string | null,
): EmojiLikeableEntity {
    // Unreact is handled explicitly via emojiType=null from the component.
    // Clicking the same emoji in picker is a no-op (keeps current reaction).
    const prevEmojiType = current.userReaction;
    const effectiveEmojiType = newEmojiType;

    const newBreakdown: Record<string, number> = { ...(current.reactionsBreakdown || {}) };

    if (prevEmojiType) {
        newBreakdown[prevEmojiType] = (newBreakdown[prevEmojiType] || 1) - 1;
        if (newBreakdown[prevEmojiType] <= 0) {
            delete newBreakdown[prevEmojiType];
        }
    }
    if (effectiveEmojiType) {
        newBreakdown[effectiveEmojiType] = (newBreakdown[effectiveEmojiType] || 0) + 1;
    }

    const newTotalCount = Object.values(newBreakdown).reduce((sum, n) => sum + n, 0);

    // Like = Reaction now, so isLiked/likesCount mirror reactions directly
    const newIsLiked = effectiveEmojiType !== null;
    const newLikesCount = newTotalCount;

    return {
        ...current,
        userReaction: effectiveEmojiType,
        reactionsBreakdown: newBreakdown,
        reactionsTotalCount: newTotalCount,
        isLiked: newIsLiked,
        likesCount: newLikesCount,
    };
}

export function* saveReaction({
    entity,
    id,
    emojiType,
    xhr,
    history,
    location,
    routeNavigate,
}: ReactionChangeAction & XHRAction & HistoryAction) {
    const ACTION_TO_REACT = "Reakcia"; // will be replaced with translation in Phase 6
    if (!(yield call(checkIfUserIsLoggedIn, routeNavigate, location))) {
        return;
    }

    const key = likeKey(entity, id);
    const current: EmojiLikeableEntity = yield select(getEntitiesEntity, "likeable", key);

    if (!current) {
        return;
    }

    // Same emoji as current = no-op
    if (emojiType !== null && emojiType === current.userReaction) {
        return;
    }

    const effectiveEmojiType = emojiType;

    const optimisticState = computeOptimisticState(current, emojiType);

    // Save original state for rollback on failure
    const prevResponse: NormalizedResponse = {
        result: null,
        entities: {
            likeable: { [key]: current },
        },
    };

    const fakeResponse: NormalizedResponse = {
        result: null,
        entities: {
            likeable: { [key]: optimisticState },
        },
    };

    yield put(reactionSave.request(entity, id, fakeResponse));

    try {
        const response = yield call(apiSaveReaction, xhr, entity, id, effectiveEmojiType);
        // API confirms authoritative userReaction; reactionsBreakdown/reactionsTotalCount in response are stale
        const confirmedState: EmojiLikeableEntity = {
            ...optimisticState,
            userReaction: response.body.userReaction,
        };
        const successResponse: NormalizedResponse = {
            result: null,
            entities: {
                likeable: { [key]: confirmedState },
            },
        };
        yield put(reactionSave.success(entity, id, successResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, location, routeNavigate, logger);
        yield put(reactionSave.failure(entity, id, error, prevResponse)); // prevResponse reverts optimistic update
    }
}

export default function* root() {
    yield all([takeEvery(REACTION_CHANGE, saveReaction)]);
}
